import { Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import SubmissionsHistoryLink from './SubmissionsHistoryLink'

import { ExperienceSpotsCards } from './ExperienceSpotsCards'
import { ExperienceSpotsList } from './ExperienceSpotsList'
import { AgamiSpot } from '../types/supabase'

interface ExperienceSpotsSectionsProps {
  published: AgamiSpot[]
  /**
   * What the Bozze section renders (05.4): proposal-group representatives
   * first, working drafts after. NOT the true draft count — duplicates are
   * collapsed — which is why `draftsTotal` travels separately.
   */
  drafts: AgamiSpot[]
  /** The real number of drafts, collapsed duplicates included: the heading counts these. */
  draftsTotal: number
  /** Drafts with a pending submission (05.6), already derived by the container. */
  pendingCount: number
  /** The history link (12.3): present with `view_submissions`, absent otherwise. */
  canViewSubmissions: boolean
  experienceId: string
  agamiListLoading: boolean
  vizMode: string
  removeFromExperience: (agami: AgamiSpot) => void
  showQrCode?: boolean
  /** Whether the caller holds `moderate` (05.4): without it the actions are ABSENT, not disabled. */
  canModerate?: boolean
  onRejectProposal?: (agami: AgamiSpot) => void
}

/**
 * The published/drafts sections of the experience view (05.3), for both viz
 * modes. The partition is computed ONCE by the container and consumed here:
 * this component only decides what to show.
 *
 * With no drafts there are no section headings — the page is the pre-05.3 one:
 * an empty section is noise, and for almost every experience it is the normal
 * case. Two separate tables rather than groups inside one: each renders its own
 * rows and ExperienceSpotsList needs no structural change.
 */
const ExperienceSpotsSections: React.FC<ExperienceSpotsSectionsProps> = ({
  published,
  drafts,
  draftsTotal,
  pendingCount,
  canViewSubmissions,
  experienceId,
  agamiListLoading,
  vizMode,
  removeFromExperience,
  showQrCode,
  canModerate,
  onRejectProposal,
}) => {
  const { t } = useTranslation()

  const renderView = (agamiList: AgamiSpot[]) => (
    <>
      {vizMode === 'cards' && (
        <ExperienceSpotsCards
          agamiListLoading={agamiListLoading}
          agamiList={agamiList}
          removeFromExperience={removeFromExperience}
          showQrCode={showQrCode}
          canModerate={canModerate}
          onRejectProposal={onRejectProposal}
        />
      )}

      {vizMode === 'table' && (
        <ExperienceSpotsList
          agamiListLoading={agamiListLoading}
          agamiList={agamiList}
          removeFromExperience={removeFromExperience}
          canModerate={canModerate}
          onRejectProposal={onRejectProposal}
        />
      )}
    </>
  )

  return (
    <>
      {drafts.length > 0 && published.length > 0 && (
        <Typography variant="h6" data-testid="published-section-title">
          {t('dashboardExperienceDetail.publishedSection')}{' '}
          <span className="text-base font-normal text-gray-600 dark:text-base-gray-500">
            ({published.length})
          </span>
        </Typography>
      )}

      {(drafts.length === 0 || published.length > 0) && renderView(published)}

      {/* With no drafts there is no heading to host the link, and a history
          worth reading exists precisely when everything has been decided. */}
      {drafts.length === 0 && canViewSubmissions && (
        <div className="mt-4">
          <SubmissionsHistoryLink experienceId={experienceId} variant="standalone" />
        </div>
      )}

      {drafts.length > 0 && (
        <>
          <Typography variant="h6" className="mt-4" data-testid="drafts-section-title">
            {t('dashboardExperienceDetail.draftsCount', { count: draftsTotal })}
            {/* Informative count only. Proposals already live inside Bozze and
                their history has its own explicit link; a hidden filter here
                would create state with no visible control. */}
            {pendingCount > 0 && (
              <>
                {' · '}
                <span
                  data-testid="proposals-count"
                  className="font-normal text-amber-900 dark:text-amber-300"
                >
                  {t('dashboardExperienceDetail.proposalsCount', { count: pendingCount })}
                </span>
              </>
            )}
            {/* The history (12.3), a small link beside the counter. */}
            {canViewSubmissions && (
              <>
                {' · '}
                <SubmissionsHistoryLink experienceId={experienceId} />
              </>
            )}
          </Typography>

          {renderView(drafts)}
        </>
      )}
    </>
  )
}

export default ExperienceSpotsSections
